import type { Map as MapLibreMap } from 'maplibre-gl'

import { ImageryLayerSettings } from '@types'

import {
  DEFAULT_RASTER_OPACITY,
  DEFAULT_TILE_SIZE,
  ESRI_WORLD_IMAGERY_MAX_ZOOM,
  ESRI_WORLD_IMAGERY_URL,
} from './constants'

/**
 * Возвращает массив URL-шаблонов тайлов для imagery-слоя
 * в зависимости от провайдера.
 */
export function buildImageryTiles(settings: ImageryLayerSettings): string[] {
  switch (settings.provider) {
    case 'esri-world-imagery':
      return [ESRI_WORLD_IMAGERY_URL]
    default: {
      // На будущее: можно расширить список провайдеров
      throw new Error(`Unsupported imagery provider: ${settings.provider}`)
    }
  }
}

/**
 * Инициализирует imagery-источник и слой на карте
 *
 * - Не добавляет источник, если он уже существует
 * - Не добавляет слой, если он уже существует
 * - Применяет видимость слоя согласно `enabledByDefault`
 */
export function initializeImageryLayer(map: MapLibreMap, settings: ImageryLayerSettings): void {
  const { sourceId, layerId, opacity, enabledByDefault } = settings

  const tiles = buildImageryTiles(settings)

  if (!map.getSource(sourceId)) {
    map.addSource(sourceId, {
      type: 'raster',
      tiles,
      tileSize: DEFAULT_TILE_SIZE,
      // Не запрашиваем тайлы выше максимального зума Esri — карта будет растягивать
      // последние доступные тайлы без \"Map Data Not Yet Available\".
      maxzoom: ESRI_WORLD_IMAGERY_MAX_ZOOM,
    })
  }

  if (!map.getLayer(layerId)) {
    // Стараемся добавить imagery ПОД маркеры, чтобы не \"выедать\" их прозрачностью.
    const beforeId =
      map.getLayer('markers-circles')?.id ?? map.getLayer('markers-labels')?.id ?? undefined

    map.addLayer(
      {
        id: layerId,
        type: 'raster',
        source: sourceId,
        paint: {
          'raster-opacity': opacity ?? DEFAULT_RASTER_OPACITY,
        },
      },
      beforeId
    )

    if (enabledByDefault === false) {
      map.setLayoutProperty(layerId, 'visibility', 'none')
    }
  }
}

/**
 * Переключает видимость imagery-слоя
 */
export function toggleImageryVisibility(map: MapLibreMap, layerId: string, visible: boolean): void {
  const visibility = visible ? 'visible' : 'none'
  map.setLayoutProperty(layerId, 'visibility', visibility)
}
